import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { compressImage } from "@/utils/compressImage";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import DoctorReview from "@/components/DoctorReview";
import { ImageLightbox } from "@/components/ImageLightbox";
import {
  Eye, Plus, Trash2, Search, Camera, ChevronDown, ChevronUp,
  FileText, AlertCircle, CheckCircle, Clock, ArrowLeft, Upload, Info, Edit2, X, Printer, Download
} from "lucide-react";
import BottomNav from "./BottomNav";
import { AppSidebar, SidebarMenuButton } from "./AppSidebar";

const authFetch = async (url, options = {}) => {
  const token = localStorage.getItem("session_token");
  return fetch(url, {
    ...options,
    headers: { ...options.headers, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

export default function GravesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCase, setSelectedCase] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [caseLoading, setCaseLoading] = useState(false);

  // New patient dialog
  const [newPatientOpen, setNewPatientOpen] = useState(false);
  const [patientForm, setPatientForm] = useState({ patient_name: "", patient_surname: "", patient_id: "" });

  // Upload state
  const [uploadingFor, setUploadingFor] = useState(null); // case_id
  const [photos, setPhotos] = useState({ frontal: null, right_profile: null, left_profile: null });
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(null); // session_id

  // Guidelines
  const [guidelinesOpen, setGuidelinesOpen] = useState(false);

  // Edit case
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ patient_name: "", patient_surname: "", patient_id: "" });
  const [saving, setSaving] = useState(false);

  // Comparison
  const [compareMode, setCompareMode] = useState(false);
  const [compareA, setCompareA] = useState(null);
  const [compareB, setCompareB] = useState(null);

  // Lightbox
  const [lightboxImage, setLightboxImage] = useState(null);

  const fetchCases = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/graves/cases`);
      if (res.ok) setCases(await res.json());
    } catch (e) { console.error("Failed to fetch cases:", e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCases(); }, [fetchCases]);

  const fetchCase = async (caseId) => {
    setCaseLoading(true);
    try {
      const res = await authFetch(`${API}/graves/cases/${caseId}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedCase(data.case);
        setSessions(data.sessions);
      }
    } catch (e) { console.error("Failed to fetch case:", e); }
    finally { setCaseLoading(false); }
  };

  const handleCreatePatient = async (e) => {
    e.preventDefault();
    try {
      const res = await authFetch(`${API}/graves/cases`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patientForm),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success("Patsient lisatud");
        setNewPatientOpen(false);
        setPatientForm({ patient_name: "", patient_surname: "", patient_id: "" });
        fetchCases();
        fetchCase(data.case_id);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Lisamine ebaõnnestus");
      }
    } catch (e) { toast.error("Võrguühenduse viga"); }
  };

  const handleUploadPhotos = async () => {
    if (!photos.frontal || !photos.right_profile || !photos.left_profile) {
      toast.error("Kõik 3 pilti on kohustuslikud");
      return;
    }
    setUploading(true);
    try {
      const [cFrontal, cRight, cLeft] = await Promise.all([
        compressImage(photos.frontal),
        compressImage(photos.right_profile),
        compressImage(photos.left_profile),
      ]);
      const formData = new FormData();
      formData.append("frontal", cFrontal);
      formData.append("right_profile", cRight);
      formData.append("left_profile", cLeft);
      const res = await authFetch(`${API}/graves/cases/${uploadingFor}/sessions`, {
        method: "POST",
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        toast.success("Pildid üles laetud!");
        setPhotos({ frontal: null, right_profile: null, left_profile: null });
        setUploadingFor(null);
        fetchCase(selectedCase.case_id);
        // Auto-analyze
        handleAnalyze(selectedCase.case_id, data.session_id);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Üleslaadimine ebaõnnestus");
      }
    } catch (e) { toast.error("Üleslaadimine ebaõnnestus"); }
    finally { setUploading(false); }
  };

  const handleAnalyze = async (caseId, sessionId) => {
    setAnalyzing(sessionId);
    try {
      const res = await authFetch(`${API}/graves/cases/${caseId}/sessions/${sessionId}/analyze`, {
        method: "POST",
      });
      if (res.ok) {
        toast.success("Analüüs valmis!");
        fetchCase(caseId);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Analüüs ebaõnnestus");
      }
    } catch (e) { toast.error("Analüüs ebaõnnestus"); }
    finally { setAnalyzing(null); }
  };

  const handleDeleteCase = async (caseId) => {
    if (!window.confirm("Kas olete kindel? Kõik selle patsiendi andmed ja pildid kustutatakse.")) return;
    try {
      const res = await authFetch(`${API}/graves/cases/${caseId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Patsient kustutatud");
        setSelectedCase(null);
        setSessions([]);
        fetchCases();
      }
    } catch (e) { toast.error("Kustutamine ebaõnnestus"); }
  };

  const openEditCase = () => {
    setEditForm({
      patient_name: selectedCase.patient_name,
      patient_surname: selectedCase.patient_surname,
      patient_id: selectedCase.patient_id,
    });
    setEditDialogOpen(true);
  };

  const handleEditCase = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await authFetch(`${API}/graves/cases/${selectedCase.case_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editForm),
      });
      if (res.ok) {
        toast.success("Andmed uuendatud");
        setEditDialogOpen(false);
        fetchCase(selectedCase.case_id);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Uuendamine ebaõnnestus");
      }
    } catch (e) { toast.error("Võrguühenduse viga"); }
    finally { setSaving(false); }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm("Kas olete kindel?")) return;
    try {
      const res = await authFetch(`${API}/graves/cases/${selectedCase.case_id}/sessions/${sessionId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Sessioon kustutatud");
        fetchCase(selectedCase.case_id);
      }
    } catch (e) { toast.error("Kustutamine ebaõnnestus"); }
  };

  const [notifyingDoctor, setNotifyingDoctor] = useState(null);
  const handleNotifyDoctor = async (caseId, sessionId) => {
    setNotifyingDoctor(sessionId);
    try {
      const res = await authFetch(`${API}/graves/cases/${caseId}/sessions/${sessionId}/notify-doctor`, { method: "POST" });
      if (res.ok) {
        toast.success("E-post saadetud arstile");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Saatmine ebaõnnestus");
      }
    } catch (e) { toast.error("E-posti saatmine ebaõnnestus"); }
    finally { setNotifyingDoctor(null); }
  };

  const getImageUrl = (path) => `${API}/graves/image/${path}`;

  const filteredCases = cases.filter(c => {
    const q = searchQuery.toLowerCase();
    return c.patient_name.toLowerCase().includes(q) ||
      c.patient_surname.toLowerCase().includes(q) ||
      c.patient_id.includes(q);
  });

  // Session detail view
  if (selectedCase) {
    return (
      <div className="min-h-screen bg-slate-50 pb-16 md:pb-0" data-testid="graves-case-detail">
        <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="md:ml-64 min-h-screen">
          <header className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 md:px-6 py-3">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-2 mb-2">
                <SidebarMenuButton onClick={() => setSidebarOpen(true)} />
                <Button variant="ghost" size="sm" onClick={() => { setSelectedCase(null); setSessions([]); fetchCases(); }} data-testid="back-to-list">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Tagasi
                </Button>
                <div className="flex-1 min-w-0">
                  <h1 className="text-base md:text-xl font-bold text-slate-900 truncate">
                    {selectedCase.patient_name} {selectedCase.patient_surname}
                  </h1>
                  <p className="text-xs md:text-sm text-slate-500">IK: {selectedCase.patient_id}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={openEditCase} data-testid="edit-case-btn">
                  <Edit2 className="w-4 h-4 mr-1" /> Muuda
                </Button>
                <Button size="sm" onClick={() => setUploadingFor(selectedCase.case_id)} className="bg-slate-900 hover:bg-slate-800" data-testid="new-session-btn">
                  <Camera className="w-4 h-4 mr-1" /> Uued pildid
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => handleDeleteCase(selectedCase.case_id)} data-testid="delete-case-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </header>

          <main className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
            {/* Guidelines */}
            <GuidelinesCard open={guidelinesOpen} onToggle={() => setGuidelinesOpen(!guidelinesOpen)} />

            {/* Upload Form */}
            {uploadingFor && (
              <UploadCard
                photos={photos}
                setPhotos={setPhotos}
                uploading={uploading}
                onUpload={handleUploadPhotos}
                onCancel={() => { setUploadingFor(null); setPhotos({ frontal: null, right_profile: null, left_profile: null }); }}
              />
            )}

            {/* Comparison toggle */}
            {sessions.length >= 2 && (
              <div className="flex items-center gap-3">
                <Button variant={compareMode ? "default" : "outline"} size="sm" onClick={() => { setCompareMode(!compareMode); setCompareA(null); setCompareB(null); }} data-testid="compare-toggle">
                  {compareMode ? "Sulge võrdlus" : "Võrdle sessioone"}
                </Button>
                {compareMode && <span className="text-sm text-slate-500">Valige 2 sessiooni võrdlemiseks</span>}
              </div>
            )}

            {/* Comparison view */}
            {compareMode && compareA && compareB && (
              <ComparisonView a={compareA} b={compareB} getImageUrl={getImageUrl} />
            )}

            {/* Sessions */}
            {caseLoading ? (
              <div className="flex items-center justify-center h-32"><div className="w-8 h-8 spinner" /></div>
            ) : sessions.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="p-8 text-center">
                  <Camera className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600 font-medium">Pilte pole veel üles laetud</p>
                  <p className="text-sm text-slate-400 mt-1">Klikkige "Uued pildid" nuppu alustamiseks</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {sessions.map((sess, idx) => (
                  <SessionCard
                    key={sess.session_id}
                    session={sess}
                    caseId={selectedCase.case_id}
                    index={sessions.length - idx}
                    analyzing={analyzing === sess.session_id}
                    onAnalyze={() => handleAnalyze(selectedCase.case_id, sess.session_id)}
                    onDelete={() => handleDeleteSession(sess.session_id)}
                    onNotifyDoctor={() => handleNotifyDoctor(selectedCase.case_id, sess.session_id)}
                    notifyingDoctor={notifyingDoctor === sess.session_id}
                    getImageUrl={getImageUrl}
                    onImageClick={setLightboxImage}
                    compareMode={compareMode}
                    isCompareA={compareA?.session_id === sess.session_id}
                    isCompareB={compareB?.session_id === sess.session_id}
                    onSelectCompare={() => {
                      if (!compareA || compareA.session_id === sess.session_id) setCompareA(compareA?.session_id === sess.session_id ? null : sess);
                      else if (!compareB || compareB.session_id === sess.session_id) setCompareB(compareB?.session_id === sess.session_id ? null : sess);
                      else { setCompareA(compareB); setCompareB(sess); }
                    }}
                    onRefreshSessions={() => fetchCase(selectedCase.case_id)}
                  />
                ))}
              </div>
            )}
          </main>
        </div>

        {/* Image Lightbox */}
        {lightboxImage && (
          <ImageLightbox
            src={lightboxImage.src}
            alt={lightboxImage.label}
            onClose={() => setLightboxImage(null)}
          />
        )}

        {/* Edit Case Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Muuda patsiendi andmeid</DialogTitle></DialogHeader>
            <form onSubmit={handleEditCase} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Eesnimi *</Label>
                  <Input value={editForm.patient_name} onChange={e => setEditForm({ ...editForm, patient_name: e.target.value })} required data-testid="edit-graves-name" />
                </div>
                <div className="space-y-2">
                  <Label>Perekonnanimi *</Label>
                  <Input value={editForm.patient_surname} onChange={e => setEditForm({ ...editForm, patient_surname: e.target.value })} required data-testid="edit-graves-surname" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Isikukood (11 numbrit) *</Label>
                <Input value={editForm.patient_id} onChange={e => setEditForm({ ...editForm, patient_id: e.target.value.replace(/\D/g, "").slice(0, 11) })} required maxLength={11} data-testid="edit-graves-id" />
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setEditDialogOpen(false)}>Tühista</Button>
                <Button type="submit" disabled={saving} className="flex-1 bg-slate-900 hover:bg-slate-800" data-testid="edit-graves-submit">
                  {saving ? "Salvestan..." : "Salvesta"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <BottomNav />
      </div>
    );
  }

  // Cases list view
  return (
    <div className="min-h-screen bg-slate-50 pb-16 md:pb-0" data-testid="graves-page">
      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="md:ml-64 min-h-screen">
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 md:px-6 py-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <SidebarMenuButton onClick={() => setSidebarOpen(true)} />
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-slate-900">Gravesi orbitopaatia</h1>
                <p className="text-sm text-slate-500">{cases.length} patsienti</p>
              </div>
            </div>
            <Button onClick={() => setNewPatientOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="add-patient-btn">
              <Plus className="w-4 h-4 mr-2" /> Lisa patsient
            </Button>
          </div>
        </header>

        <main className="max-w-6xl mx-auto p-4 md:p-6 space-y-4">
          <GuidelinesCard open={guidelinesOpen} onToggle={() => setGuidelinesOpen(!guidelinesOpen)} />

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Otsi nime või isikukoodi..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-testid="graves-search" />
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64"><div className="w-8 h-8 spinner" /></div>
          ) : filteredCases.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="p-12 text-center">
                <Eye className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  {searchQuery ? "Patsiente ei leitud" : "Patsiente pole veel lisatud"}
                </h3>
                <p className="text-sm text-slate-500 mb-4">
                  {searchQuery ? "Proovige muuta otsingut" : "Lisage uus patsient alustamiseks"}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredCases.map(c => (
                <Card key={c.case_id} className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => fetchCase(c.case_id)} data-testid={`case-${c.case_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-slate-900">{c.patient_name} {c.patient_surname}</h3>
                        <p className="text-sm text-slate-500">IK: {c.patient_id}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-600">{c.session_count} sessiooni</p>
                        {c.last_session?.cas_score != null && (
                          <Badge className={c.last_session.cas_score >= 3 ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}>
                            CAS {c.last_session.cas_score}/7
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* New Patient Dialog */}
      <Dialog open={newPatientOpen} onOpenChange={setNewPatientOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Lisa uus patsient</DialogTitle></DialogHeader>
          <form onSubmit={handleCreatePatient} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Eesnimi *</Label>
                <Input value={patientForm.patient_name} onChange={e => setPatientForm({ ...patientForm, patient_name: e.target.value })} required data-testid="graves-patient-name" />
              </div>
              <div className="space-y-2">
                <Label>Perekonnanimi *</Label>
                <Input value={patientForm.patient_surname} onChange={e => setPatientForm({ ...patientForm, patient_surname: e.target.value })} required data-testid="graves-patient-surname" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Isikukood (11 numbrit) *</Label>
              <Input value={patientForm.patient_id} onChange={e => setPatientForm({ ...patientForm, patient_id: e.target.value.replace(/\D/g, "").slice(0, 11) })} required maxLength={11} data-testid="graves-patient-id" />
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setNewPatientOpen(false)}>Tühista</Button>
              <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800" data-testid="graves-submit-patient">Lisa</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
}


// ==================== Sub-components ====================

function GuidelinesCard({ open, onToggle }) {
  return (
    <Card className="border-slate-200 border-l-4 border-l-sky-400" data-testid="guidelines-card">
      <CardContent className="p-4">
        <button onClick={onToggle} className="w-full flex items-center justify-between text-left" data-testid="guidelines-toggle">
          <div className="flex items-center gap-2">
            <Info className="w-5 h-5 text-sky-600" />
            <span className="font-semibold text-slate-800">Pildistamise juhised</span>
          </div>
          {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>
        {open && (
          <div className="mt-4 space-y-4 text-sm text-slate-600">
            <div>
              <h4 className="font-semibold text-slate-800 mb-1">1. Keskkonna ettevalmistus</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>Kasutage hajutatud, kaudset valgustust v ringvalgust</li>
                <li>Veenduge, et poleks suunatud varje</li>
                <li>Kasutage heledat, matti, helesinist v halli tausta</li>
                <li>Puhastage kaamera objektiiv enne iga patsienti</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-slate-800 mb-1">2. Patsiendi ettevalmistus</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>Juuksed peapaela abil kõrvade taha</li>
                <li>Eemaldage prillid, tugev silmameik, ehted</li>
                <li>Patsient hoidku lõdvestunud, neutraalset ilmet (mitte naeratada ega kissitada)</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-slate-800 mb-1">3. Pildistamise järjekord</h4>
              <div className="bg-slate-50 rounded-lg p-3 font-mono text-center text-xs my-2">
                [Vasak profiil] &lt;== [Frontaalvaade] ==&gt; [Parem profiil]
              </div>
              <div className="space-y-2">
                <div>
                  <span className="font-medium text-slate-700">Pilt 1 - Frontaalvaade:</span>
                  <span className="ml-1">Patsient vaatab otse kaamerasse, kaamera silma kõrgusel</span>
                </div>
                <div>
                  <span className="font-medium text-slate-700">Pilt 2 - Parem profiil:</span>
                  <span className="ml-1">Patsient pöörab pea 90° vasakule, vaatab otse (mitte tagasi kaamerasse)</span>
                </div>
                <div>
                  <span className="font-medium text-slate-700">Pilt 3 - Vasak profiil:</span>
                  <span className="ml-1">Patsient pöörab pea 90° paremale, vaatab otse</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-slate-800 mb-1">4. Kvaliteedikontroll</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>Kontrollige 3 pilti enne salvestamist</li>
                <li>Udune, suletud silmadega v viltu pea korral tehke uus pilt</li>
              </ul>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function UploadCard({ photos, setPhotos, uploading, onUpload, onCancel }) {
  const labels = [
    { key: "frontal", label: "Frontaalvaade (eest)", icon: "F" },
    { key: "right_profile", label: "Parem profiil", icon: "P" },
    { key: "left_profile", label: "Vasak profiil", icon: "V" },
  ];

  return (
    <Card className="border-slate-200 border-l-4 border-l-emerald-400" data-testid="upload-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Upload className="w-5 h-5 text-emerald-600" /> Laadi pildid üles
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {labels.map(({ key, label, icon }) => (
            <PhotoInput
              key={key}
              label={label}
              icon={icon}
              file={photos[key]}
              onSelect={(f) => setPhotos({ ...photos, [key]: f })}
            />
          ))}
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onCancel} className="flex-1">Tühista</Button>
          <Button
            onClick={onUpload}
            disabled={uploading || !photos.frontal || !photos.right_profile || !photos.left_profile}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="upload-photos-btn"
          >
            {uploading ? <span className="w-4 h-4 spinner mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? "Laadin..." : "Laadi üles ja analüüsi"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


function PhotoInput({ label, icon, file, onSelect }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreview(null);
  }, [file]);

  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium text-slate-600">{label}</Label>
      <div
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-slate-300 rounded-lg p-3 text-center cursor-pointer hover:border-emerald-400 hover:bg-emerald-50/50 transition-colors aspect-[4/3] flex flex-col items-center justify-center relative overflow-hidden"
      >
        {preview ? (
          <img src={preview} alt={label} className="absolute inset-0 w-full h-full object-cover rounded-lg" />
        ) : (
          <>
            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center mb-2">
              <span className="text-sm font-bold text-slate-500">{icon}</span>
            </div>
            <p className="text-xs text-slate-400">Kliki v lohista</p>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
          className="hidden"
          onChange={(e) => { if (e.target.files[0]) onSelect(e.target.files[0]); }}
        />
      </div>
    </div>
  );
}


function SessionCard({ session, caseId, index, analyzing, onAnalyze, onDelete, onNotifyDoctor, notifyingDoctor, getImageUrl, onImageClick, compareMode, isCompareA, isCompareB, onSelectCompare, onRefreshSessions }) {
  const [expanded, setExpanded] = useState(index === 1);
  const date = new Date(session.created_at).toLocaleString("et-EE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

  const handlePdf = async (action) => {
    try {
      toast.info(action === "print" ? "Genereerin..." : "Laen alla...");
      const res = await authFetch(`${API}/graves/cases/${caseId}/sessions/${session.session_id}/pdf`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (action === "print") {
        const w = window.open(url);
        if (w) w.onload = () => w.print();
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `Graves_raport_${session.session_id.slice(-6)}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch {
      toast.error("PDF genereerimine ebaõnnestus");
    }
  };

  const statusBadge = session.status === "completed"
    ? <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" /> Analüüsitud</Badge>
    : session.status === "failed"
      ? <Badge className="bg-red-100 text-red-700"><AlertCircle className="w-3 h-3 mr-1" /> Ebaõnnestunud</Badge>
      : <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" /> Ootel</Badge>;

  const needsReview = session.status === "completed" && !session.doctor_reviewed;

  return (
    <Card className={`${needsReview ? "border-red-300 bg-red-50/30" : "border-slate-200"} ${isCompareA ? "ring-2 ring-sky-400" : ""} ${isCompareB ? "ring-2 ring-violet-400" : ""}`} data-testid={`session-${session.session_id}`}>
      <CardContent className="p-3 md:p-4">
        {/* Row 1: Session title + expand */}
        <div className="flex items-center justify-between mb-1">
          <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-2">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            <span className="font-semibold text-slate-900 text-sm md:text-base">Sessioon #{index}</span>
          </button>
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] md:text-xs text-slate-400 whitespace-nowrap">{date}</span>
            <Button size="sm" variant="outline" className="text-red-600 border-red-200 h-7 w-7 p-0" onClick={onDelete} data-testid={`delete-session-${session.session_id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        {/* Row 2: Badges */}
        <div className="flex items-center gap-1.5 flex-wrap mb-2">
          {needsReview && (
            <Badge className="bg-red-100 text-red-700 text-[10px] md:text-xs" data-testid="nurse-upload-dot">Kontrollimata</Badge>
          )}
          {session.doctor_reviewed && (
            <Badge className="bg-emerald-100 text-emerald-700 text-[10px] md:text-xs">Kontrollitud</Badge>
          )}
          {statusBadge}
          {session.cas_score != null && (
            <Badge className={`text-[10px] md:text-xs ${session.cas_score >= 3 ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
              CAS {session.cas_score}/7
            </Badge>
          )}
          {compareMode && (
            <Button size="sm" variant={isCompareA || isCompareB ? "default" : "outline"} className="h-6 text-xs px-2" onClick={(e) => { e.stopPropagation(); onSelectCompare(); }}>
              {isCompareA ? "A" : isCompareB ? "B" : "Vali"}
            </Button>
          )}
          {session.status === "pending" && (
            <Button size="sm" variant="outline" onClick={onAnalyze} disabled={analyzing} className="text-sky-600 h-6 text-xs px-2">
              {analyzing ? <span className="w-3 h-3 spinner" /> : <><FileText className="w-3 h-3 mr-1" /> Analüüsi</>}
            </Button>
          )}
          {session.status === "failed" && (
            <Button size="sm" variant="outline" onClick={onAnalyze} disabled={analyzing} className="text-amber-600 h-6 text-xs px-2">
              {analyzing ? <span className="w-3 h-3 spinner" /> : "Uuesti"}
            </Button>
          )}
        </div>
        {expanded && (
          <div className="space-y-4 mt-3">
            <div className="grid grid-cols-3 gap-3">
              {[
                { path: session.frontal_image, label: "Eestvaade" },
                { path: session.right_image, label: "Parem profiil" },
                { path: session.left_image, label: "Vasak profiil" },
              ].map(({ path, label }) => (
                <div key={path} className="space-y-1">
                  <p className="text-xs text-slate-500 text-center">{label}</p>
                  <AuthImage src={getImageUrl(path)} alt={label} className="w-full aspect-[4/3] object-cover rounded-lg border border-slate-200" onClick={() => onImageClick && onImageClick({ src: getImageUrl(path), label })} />
                </div>
              ))}
            </div>
            {session.result_hidden ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center" data-testid="result-hidden-notice">
                <Clock className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <p className="font-semibold text-amber-800">Analüüs teostatud</p>
                <p className="text-sm text-amber-600 mt-1">Ootab arsti kontrolli. Tulemus muutub nähtavaks pärast arsti kinnitust.</p>
              </div>
            ) : session.analysis_result ? (
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-sky-600" /> AI analüüsi tulemus
                  <button onClick={() => handlePdf("print")} className="ml-auto p-1 hover:bg-slate-200 rounded" title="Prindi" data-testid="graves-print-btn">
                    <Printer className="w-4 h-4 text-slate-500" />
                  </button>
                  <button onClick={() => handlePdf("download")} className="p-1 hover:bg-slate-200 rounded" title="Lae alla" data-testid="graves-download-btn">
                    <Download className="w-4 h-4 text-slate-500" />
                  </button>
                </h4>
                <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {session.analysis_result}
                </div>
                <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700" data-testid="ai-disclaimer">
                  <strong>NB:</strong> See on tehisintellekti (Gemini 3.1 Pro Vision) esialgne hinnang. Lõpliku otsuse teeb arst. Andmeid säilitatakse 10 aastat vastavalt Eesti tervishoiuteenuste korraldamise seadusele (§ 769).
                </div>
              </div>
            ) : null}
            {/* Doctor Review */}
            {session.status === "completed" && !session.result_hidden && (
              <DoctorReview
                reviewUrl={`/graves/cases/${caseId}/sessions/${session.session_id}/doctor-review`}
                sendUrl={`/graves/cases/${caseId}/sessions/${session.session_id}/send-review-to-nurse`}
                record={session}
                onUpdate={onRefreshSessions}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function AuthImage({ src, alt, className, onClick }) {
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("session_token");
    fetch(src, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(res => res.ok ? res.blob() : null)
      .then(blob => {
        if (blob && !cancelled) setBlobUrl(URL.createObjectURL(blob));
      })
      .catch(() => {});
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [src]); // eslint-disable-line

  if (!blobUrl) return <div className={`${className} bg-slate-100 flex items-center justify-center`}><Camera className="w-6 h-6 text-slate-300" /></div>;
  return <img src={blobUrl} alt={alt} className={`${className} ${onClick ? "cursor-zoom-in" : ""}`} onClick={onClick} />;
}


function ComparisonView({ a, b, getImageUrl }) {
  const dateA = new Date(a.created_at).toLocaleDateString("et-EE");
  const dateB = new Date(b.created_at).toLocaleDateString("et-EE");

  return (
    <Card className="border-slate-200 border-l-4 border-l-violet-400" data-testid="comparison-view">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Sessioonide võrdlus</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-sky-600 mb-2">A: {dateA} {a.cas_score != null && `(CAS ${a.cas_score}/7)`}</p>
            <div className="grid grid-cols-3 gap-2">
              <AuthImage src={getImageUrl(a.frontal_image)} alt="A front" className="w-full aspect-[4/3] object-cover rounded border" />
              <AuthImage src={getImageUrl(a.right_image)} alt="A right" className="w-full aspect-[4/3] object-cover rounded border" />
              <AuthImage src={getImageUrl(a.left_image)} alt="A left" className="w-full aspect-[4/3] object-cover rounded border" />
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-violet-600 mb-2">B: {dateB} {b.cas_score != null && `(CAS ${b.cas_score}/7)`}</p>
            <div className="grid grid-cols-3 gap-2">
              <AuthImage src={getImageUrl(b.frontal_image)} alt="B front" className="w-full aspect-[4/3] object-cover rounded border" />
              <AuthImage src={getImageUrl(b.right_image)} alt="B right" className="w-full aspect-[4/3] object-cover rounded border" />
              <AuthImage src={getImageUrl(b.left_image)} alt="B left" className="w-full aspect-[4/3] object-cover rounded border" />
            </div>
          </div>
        </div>
        {a.cas_score != null && b.cas_score != null && (
          <div className="bg-slate-50 rounded-lg p-3 text-center">
            <p className="text-sm text-slate-600">
              CAS muutus: <span className="font-bold">{a.cas_score}/7</span> → <span className="font-bold">{b.cas_score}/7</span>
              {b.cas_score < a.cas_score && <span className="text-emerald-600 ml-2">Paranemine</span>}
              {b.cas_score > a.cas_score && <span className="text-red-600 ml-2">Halvenemine</span>}
              {b.cas_score === a.cas_score && <span className="text-slate-500 ml-2">Muutuseta</span>}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
